import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import Nav from "../components/Nav";
import api from "../lib/api";
import { toast } from "sonner";
import VideoDashboardTab from "./VideoDashboardTab";
import AmbassadorDashboardTab from "./AmbassadorDashboardTab";
import WeeklyReviewsTab from "./WeeklyReviewsTab";
import GullyPMTab from "./GullyPMTab";

function fmtTime(s) {
  if (!s) return "—";
  try {
    const d = new Date(s);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return `${Math.round(diff)}s ago`;
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
    return d.toISOString().slice(0, 16).replace("T", " ");
  } catch {
    return s;
  }
}



function SpotlightTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/ambassadors/top?limit=5");
        setItems(data.items || []);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const downloadSpotlight = async (code) => {
    setDownloading(code);
    try {
      const res = await api.get(`/admin/ambassadors/spotlight.png?code=${encodeURIComponent(code)}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "image/png" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `gully-spotlight-${code}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Spotlight for ${code} saved — ready for your story.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't generate spotlight.");
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="mb-12" data-testid="spotlight-section">
      <div className="flex items-baseline justify-between mb-4">
        <p className="label-eyebrow">ambassador spotlight</p>
        <p className="text-xs text-neutral-600 italic">
          one-click 1080×1080 story card · for @secondinnings.spinner
        </p>
      </div>

      {loading && <p className="text-neutral-600 text-xs py-4">Loading top ambassadors…</p>}

      {!loading && items.length === 0 && (
        <p className="text-neutral-600 text-xs py-4 italic" data-testid="spotlight-empty">
          No ambassadors with joins yet. Card needs at least one to celebrate.
        </p>
      )}

      {!loading && items.length > 0 && (
        <div className="grid gap-2" data-testid="spotlight-list">
          {items.map((a, idx) => {
            const joins = Number(a.verified_joins || 0);
            const isPodium = idx < 3;
            return (
              <div
                key={a.code}
                data-testid={`spotlight-row-${a.code}`}
                className="grid grid-cols-12 items-center gap-3 px-4 py-4"
                style={{
                  background: isPodium ? "rgba(167,243,208,0.04)" : "var(--surface-1)",
                  border: "1px solid var(--border-default)",
                  borderRadius: "var(--radius-md)",
                }}
              >
                <div className="col-span-1 font-mono text-neutral-500 text-xs tracking-wider">
                  #{String(idx + 1).padStart(2, "0")}
                </div>
                <div className="col-span-6">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-display text-xl text-neutral-100 tracking-tight">{a.code}</span>
                    {a.featured && (
                      <span
                        className="px-1.5 py-0.5 rounded text-[9px] uppercase tracking-widest"
                        style={{ background: "rgba(167,243,208,0.10)", color: "var(--highlight)", border: "1px solid rgba(167,243,208,0.25)" }}
                      >
                        Featured
                      </span>
                    )}
                  </div>
                </div>
                <div className="col-span-2 text-right">
                  <span className="font-display text-lg text-neutral-100">{joins}</span>
                  <span className="text-[10px] text-neutral-500 ml-1">{joins === 1 ? "cricketer" : "cricketers"}</span>
                </div>
                <div className="col-span-3 text-right">
                  <button
                    data-testid={`spotlight-download-${a.code}`}
                    onClick={() => downloadSpotlight(a.code)}
                    disabled={downloading === a.code || joins === 0}
                    className={`px-3 py-1.5 text-xs rounded transition ${joins === 0 ? "bg-neutral-900 text-neutral-600 cursor-not-allowed" : "bg-white/10 text-neutral-100 hover:bg-white/20"}`}
                    title={joins === 0 ? "No joins yet — nothing to celebrate" : "Download 1080×1080 Instagram story card"}
                  >
                    {downloading === a.code ? "Generating…" : "Download story card"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}





function IntelTab() {
  const [latest, setLatest] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const refresh = async () => {
    try {
      const [r1, r2] = await Promise.all([
        api.get("/admin/intel/latest"),
        api.get("/admin/intel?limit=10"),
      ]);
      setLatest(r1.data?.intel_id ? r1.data : null);
      setHistory(r2.data?.items || []);
    } catch {
      setLatest(null);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const generate = async () => {
    setGenerating(true);
    toast.info("Generation kicked off — takes ~60-90s. Page will auto-refresh when ready.");
    // Fire-and-forget — proxy times out at ~120s but backend persists the doc.
    // We poll for a new report instead of waiting on the response.
    api.post("/admin/intel/generate", {}, { timeout: 200000 }).catch(() => {});
    const before = latest?.intel_id;
    let tries = 0;
    const poll = setInterval(async () => {
      tries++;
      try {
        const { data } = await api.get("/admin/intel/latest");
        if (data?.intel_id && data.intel_id !== before) {
          clearInterval(poll);
          setLatest(data);
          await refresh();
          toast.success("Fresh intel report ready.");
          setGenerating(false);
        }
      } catch {}
      if (tries > 60) { // ~3 min cap
        clearInterval(poll);
        setGenerating(false);
        toast.error("Still generating in background — check back in a minute.");
      }
    }, 3000);
  };

  const r = latest?.report || {};
  const stats = latest?.stats || {};

  return (
    <div className="mb-12" data-testid="intel-section">
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-3">
        <p className="label-eyebrow">founder intelligence</p>
        <button
          data-testid="intel-generate-btn"
          onClick={generate}
          disabled={generating}
          className="btn-primary text-xs"
        >
          {generating ? "Generating…" : latest ? "Generate fresh report" : "Generate first report"}
        </button>
      </div>

      {loading && <p className="text-neutral-600 text-xs py-4">Loading…</p>}

      {!loading && !latest && (
        <p className="text-neutral-500 text-sm italic py-4" data-testid="intel-empty">
          No reports yet. Click "Generate first report" — analyses last 7 days of conversations.
        </p>
      )}

      {!loading && latest && (
        <div className="space-y-6" data-testid="intel-content">
          <div className="bento-card p-6">
            <p className="text-xs text-neutral-500 mb-2">
              {latest.window ? `${latest.window.since?.slice(0,10)} → ${latest.window.until?.slice(0,10)}` : ""}
              {" · "}
              {latest.sample_size?.messages_sampled} messages · {latest.sample_size?.conversations_sampled} convs
            </p>
            <p className="font-display text-2xl text-neutral-100 leading-snug" data-testid="intel-headline">
              {r.headline || "—"}
            </p>
          </div>

          <Section title="1 · Top Topics" testid="intel-section-topics">
            <ul className="space-y-2">
              {(r.section_1_top_topics || []).map((t, i) => (
                <li key={i} className="text-sm">
                  <span className="text-neutral-200 font-medium">{t.topic}</span>
                  <span className="text-neutral-500"> · {t.share}</span>
                  <div className="text-neutral-400 italic ml-3">{t.summary}</div>
                </li>
              ))}
            </ul>
          </Section>

          <Section title="2 · User Pain Points" testid="intel-section-pain">
            <SubList label="frustrations" items={(r.section_2_pain_points || {}).frustrations} />
            <SubList label="confidence issues" items={(r.section_2_pain_points || {}).confidence_issues} />
            <SubList label="cricket struggles" items={(r.section_2_pain_points || {}).cricket_struggles} />
          </Section>

          <Section title="3 · Product Gaps" testid="intel-section-gaps">
            <SubList label="questions Gully struggles with" items={(r.section_3_product_gaps || {}).questions_gully_struggles_with} />
            <SubList label="conversation dead ends" items={(r.section_3_product_gaps || {}).conversation_dead_ends} />
            <SubList label="repeated confusion" items={(r.section_3_product_gaps || {}).repeated_user_confusion} />
            <SubList label="missing features implied" items={(r.section_3_product_gaps || {}).missing_features_implied} />
          </Section>

          <Section title="4 · Retention Insights" testid="intel-section-retention">
            <SubList label="returning user traits" items={(r.section_4_retention_insights || {}).returning_user_traits} />
            <SubList label="one-time user traits" items={(r.section_4_retention_insights || {}).onetime_user_traits} />
            <SubList label="high-engagement topics" items={(r.section_4_retention_insights || {}).high_engagement_topics} />
          </Section>

          <Section title="5 · Content Engine" testid="intel-section-content">
            <details className="mb-3">
              <summary className="text-xs text-neutral-400 cursor-pointer hover:text-neutral-200">Top questions ({(r.section_5_content_engine || {}).top_50_questions?.length || 0})</summary>
              <ul className="mt-2 ml-3 space-y-1">
                {((r.section_5_content_engine || {}).top_50_questions || []).map((q, i) =>
                  <li key={i} className="text-sm text-neutral-300">• {q}</li>
                )}
              </ul>
            </details>
            <details className="mb-3" open>
              <summary className="text-xs text-neutral-400 cursor-pointer hover:text-neutral-200">Reel ideas ({(r.section_5_content_engine || {}).reel_ideas?.length || 0})</summary>
              <ul className="mt-2 ml-3 space-y-2">
                {((r.section_5_content_engine || {}).reel_ideas || []).map((re, i) =>
                  <li key={i} className="text-sm">
                    <div className="text-neutral-100 font-medium">🎬 {re.hook}</div>
                    <div className="text-neutral-500 italic ml-4">{re.body}</div>
                  </li>
                )}
              </ul>
            </details>
            <details className="mb-3">
              <summary className="text-xs text-neutral-400 cursor-pointer hover:text-neutral-200">Carousel ideas ({(r.section_5_content_engine || {}).carousel_ideas?.length || 0})</summary>
              <ul className="mt-2 ml-3 space-y-3">
                {((r.section_5_content_engine || {}).carousel_ideas || []).map((c, i) =>
                  <li key={i} className="text-sm">
                    <div className="text-neutral-100 font-medium">📑 {c.title}</div>
                    <ol className="ml-5 mt-1 text-xs text-neutral-400 list-decimal space-y-0.5">
                      {(c.slides || []).map((s, j) => <li key={j}>{s}</li>)}
                    </ol>
                  </li>
                )}
              </ul>
            </details>
            <details>
              <summary className="text-xs text-neutral-400 cursor-pointer hover:text-neutral-200">Emotional quotes</summary>
              <ul className="mt-2 ml-3 space-y-1">
                {((r.section_5_content_engine || {}).emotional_quotes || []).map((q, i) =>
                  <li key={i} className="text-sm">
                    <span className="text-neutral-100 italic">"{q.quote}"</span>
                    <span className="text-neutral-500 text-xs ml-2">— {q.context}</span>
                  </li>
                )}
              </ul>
            </details>
          </Section>

          <Section title="6 · Opportunity Detection" testid="intel-section-opps">
            <SubList label="requested capabilities" items={(r.section_6_opportunities || {}).requested_capabilities} />
            <SubList label="emerging themes" items={(r.section_6_opportunities || {}).emerging_themes} />
            <SubList label="new product opportunities" items={(r.section_6_opportunities || {}).new_product_opportunities} />
          </Section>

          <Section title="🎯 Founder's attention this week" testid="intel-section-attention" accent>
            <ul className="space-y-2">
              {(r.founders_attention || []).map((f, i) =>
                <li key={i} className="text-sm text-neutral-100">→ {f}</li>
              )}
            </ul>
          </Section>

          {history.length > 1 && (
            <div className="pt-4">
              <p className="label-eyebrow mb-2">past reports</p>
              <ul className="text-xs text-neutral-500 space-y-1">
                {history.slice(1).map((h) =>
                  <li key={h.intel_id}>
                    {h.generated_at?.slice(0,10)} — <span className="text-neutral-400">{h.report?.headline || h.intel_id}</span>
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ title, children, testid, accent }) {
  return (
    <div className="bento-card p-6" data-testid={testid} style={accent ? { borderLeft: "2px solid var(--highlight)" } : {}}>
      <p className="label-eyebrow mb-3">{title}</p>
      {children}
    </div>
  );
}

function SubList({ label, items }) {
  if (!items || !items.length) return null;
  return (
    <div className="mb-3 last:mb-0">
      <p className="text-[10px] uppercase tracking-widest text-neutral-600 mb-1.5">{label}</p>
      <ul className="space-y-1">
        {items.map((it, i) => <li key={i} className="text-sm text-neutral-300">• {it}</li>)}
      </ul>
    </div>
  );
}


function CheckinTool() {
  const [filter, setFilter] = useState("inactive_5");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [message, setMessage] = useState("");
  const [templateKey, setTemplateKey] = useState("");
  const [force, setForce] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(async (f) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/checkin-users?filter=${f}&limit=80`);
      setData(data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(filter); }, [filter, load]);

  const counts = data?.counts || {};
  const templates = data?.templates || [];
  const pills = [
    ["all", "All"],
    ["inactive_3", "3d+"],
    ["inactive_5", "5d+"],
    ["inactive_7", "7d+"],
    ["heavy", "Heavy"],
    ["returning", "Returning"],
    ["emotional", "Emotional"],
  ];

  const openSend = (row) => {
    setSelected(row);
    setMessage("");
    setTemplateKey("");
    setForce(false);
  };

  const closeSend = () => {
    setSelected(null);
    setMessage("");
    setTemplateKey("");
    setForce(false);
  };

  const pickTemplate = (t) => {
    setTemplateKey(t.key);
    setMessage(t.body);
  };

  const doSend = async () => {
    if (!selected || !message.trim()) return;
    setSending(true);
    try {
      const res = await api.post("/admin/checkin-send", {
        wa_number: selected.wa_number,
        message: message.trim(),
        template_key: templateKey || null,
        force,
      });
      if (res.data?.ok) {
        toast.success(
          res.data.was_in_window
            ? "Check-in sent ✓"
            : "Check-in sent (outside 24h window — Meta may reject)"
        );
        closeSend();
        load(filter);
      } else {
        toast.error(res.data?.error || "Send failed");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mb-12" data-testid="checkin-section">
      <div className="flex items-baseline justify-between mb-4">
        <p className="label-eyebrow">manual whatsapp check-in</p>
        <p className="text-xs text-neutral-600 italic">
          founder-only · max 1 per user per {data?.cooldown_days || 5} days · logged
        </p>
      </div>

      <div className="flex gap-1 mb-5 flex-wrap" data-testid="checkin-filters">
        {pills.map(([k, label]) => (
          <button
            key={k}
            data-testid={`checkin-pill-${k}`}
            onClick={() => setFilter(k)}
            className={`px-3 py-1.5 text-xs rounded transition ${filter === k ? "bg-white/10 text-neutral-100" : "text-neutral-500 hover:text-neutral-300"}`}
          >
            {label} <span className="text-neutral-600 ml-1">{counts[k] ?? 0}</span>
          </button>
        ))}
      </div>

      <div className="grid gap-1" data-testid="checkin-rows">
        {loading && <p className="text-neutral-600 text-xs py-4">Loading…</p>}
        {!loading && data?.rows?.length === 0 && (
          <p className="text-neutral-600 text-xs py-4 italic">
            No-one matches the {filter} filter.
          </p>
        )}
        {!loading && data?.rows?.map((r) => {
          const cooldownBlocked = r.cooldown_remaining_days && r.cooldown_remaining_days > 0;
          return (
            <div
              key={r.wa_number}
              data-testid={`checkin-row-${r.wa_number}`}
              className="grid grid-cols-12 gap-3 items-center py-3 px-3 hover:bg-white/[0.03] text-xs border-b border-neutral-900/60"
            >
              <div className="col-span-3">
                <div className="text-neutral-200 truncate">{r.display_name || "—"}</div>
                <div className="text-neutral-600 text-[10px] truncate">{r.wa_number}</div>
              </div>
              <div className="col-span-3 text-neutral-500 truncate italic">
                {r.last_message_preview ? `"${r.last_message_preview}"` : "—"}
              </div>
              <div className="col-span-2 text-neutral-400 text-right">
                {r.total_messages}m · {r.conversation_count}c
              </div>
              <div className="col-span-1 text-neutral-500 text-right">
                {r.days_since_last === 0 ? "today" :
                  r.days_since_last === 1 ? "1d" :
                  r.days_since_last != null ? `${r.days_since_last}d` : "—"}
              </div>
              <div className="col-span-2 flex flex-wrap gap-1 justify-end">
                <span
                  data-testid={`checkin-window-${r.wa_number}`}
                  className={`text-[10px] px-1.5 py-0.5 rounded ${r.in_24h_window ? "bg-emerald-900/40 text-emerald-300" : "bg-amber-900/30 text-amber-300"}`}
                  title={r.in_24h_window ? "Within Meta 24h customer-service window — free-form OK" : "Outside 24h window — needs approved Meta template"}
                >
                  {r.in_24h_window ? "24h ✓" : "needs tpl"}
                </span>
                {r.tags?.slice(0, 2).map((t) => (
                  <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-white/[0.04] text-neutral-400">
                    {t}
                  </span>
                ))}
              </div>
              <div className="col-span-1 text-right">
                <button
                  data-testid={`checkin-send-btn-${r.wa_number}`}
                  onClick={() => openSend(r)}
                  className={`px-3 py-1 text-xs rounded transition ${cooldownBlocked ? "bg-neutral-900 text-neutral-500" : "bg-white/10 text-neutral-100 hover:bg-white/20"}`}
                  title={cooldownBlocked ? `Cooldown — ${r.cooldown_remaining_days}d left` : "Send manual check-in"}
                >
                  {cooldownBlocked ? `${r.cooldown_remaining_days}d` : "Send"}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {selected && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-4"
          data-testid="checkin-send-modal"
          onClick={closeSend}
        >
          <div
            className="bento-card max-w-xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-baseline justify-between mb-4">
              <div>
                <p className="label-eyebrow mb-1">send manual check-in</p>
                <p className="font-display text-lg text-neutral-100">{selected.display_name || "—"}</p>
                <p className="text-xs text-neutral-500">{selected.wa_number}</p>
              </div>
              <button data-testid="checkin-close-btn" onClick={closeSend} className="text-neutral-500 hover:text-neutral-200 text-xl leading-none">×</button>
            </div>

            <div className="mb-4 flex flex-wrap gap-2 text-[10px]">
              <span className={`px-2 py-1 rounded ${selected.in_24h_window ? "bg-emerald-900/40 text-emerald-300" : "bg-amber-900/30 text-amber-300"}`}>
                {selected.in_24h_window ? "24h window OK — free-form fine" : "outside 24h — needs Meta template"}
              </span>
              {selected.last_checkin_at && (
                <span className="px-2 py-1 rounded bg-white/[0.04] text-neutral-400" data-testid="checkin-last-info">
                  last check-in {fmtTime(selected.last_checkin_at)}
                </span>
              )}
              {selected.cooldown_remaining_days > 0 && (
                <span className="px-2 py-1 rounded bg-rose-900/30 text-rose-300" data-testid="checkin-cooldown-warn">
                  cooldown {selected.cooldown_remaining_days}d left
                </span>
              )}
            </div>

            <p className="label-eyebrow mb-2">suggested templates</p>
            <div className="flex flex-wrap gap-1.5 mb-4">
              {templates.map((t) => (
                <button
                  key={t.key}
                  data-testid={`checkin-template-${t.key}`}
                  onClick={() => pickTemplate(t)}
                  className={`px-2.5 py-1.5 text-[11px] rounded transition ${templateKey === t.key ? "bg-emerald-900/40 text-emerald-200" : "bg-white/[0.04] text-neutral-300 hover:bg-white/10"}`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <p className="label-eyebrow mb-2">message</p>
            <textarea
              data-testid="checkin-message-input"
              value={message}
              onChange={(e) => { setMessage(e.target.value); setTemplateKey(""); }}
              placeholder="Write a short, human check-in…"
              rows={4}
              maxLength={1500}
              className="input-dark w-full text-sm mb-2"
            />
            <div className="flex items-center justify-between mb-4">
              <p className="text-[10px] text-neutral-600">{message.length}/1500</p>
              {(!selected.in_24h_window || selected.cooldown_remaining_days > 0) && (
                <label className="flex items-center gap-2 text-xs text-neutral-400 cursor-pointer">
                  <input
                    data-testid="checkin-force-toggle"
                    type="checkbox"
                    checked={force}
                    onChange={(e) => setForce(e.target.checked)}
                    className="accent-emerald-500"
                  />
                  override safety checks
                </label>
              )}
            </div>

            <div className="flex gap-2 justify-end">
              <button
                data-testid="checkin-cancel-btn"
                onClick={closeSend}
                className="btn-ghost text-xs"
                disabled={sending}
              >
                Cancel
              </button>
              <button
                data-testid="checkin-confirm-btn"
                onClick={doSend}
                disabled={sending || !message.trim() || (selected.cooldown_remaining_days > 0 && !force) || (!selected.in_24h_window && !force)}
                className="btn-primary text-xs"
              >
                {sending ? "Sending…" : "Send check-in"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function Cohorts() {
  const [cohort, setCohort] = useState("returning");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (c) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/cohorts?cohort=${c}&limit=40`);
      setData(data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(cohort); }, [cohort, load]);

  const counts = data?.counts || { all: 0, new: 0, returning: 0, heavy: 0, dormant: 0 };
  const pills = [
    ["all", "All", counts.all],
    ["new", "New", counts.new],
    ["returning", "Returning", counts.returning],
    ["heavy", "Heavy", counts.heavy],
    ["dormant", "Dormant", counts.dormant],
  ];

  return (
    <div className="mb-10" data-testid="cohorts-section">
      <div className="flex items-baseline justify-between mb-4">
        <p className="label-eyebrow">who's coming back</p>
        <p className="text-xs text-neutral-600 italic">
          {cohort === "returning" && "the trust signal"}
          {cohort === "new" && "first 7 days"}
          {cohort === "heavy" && "forming a habit"}
          {cohort === "dormant" && "quiet for 7+ days"}
          {cohort === "all" && "everyone"}
        </p>
      </div>
      <div className="flex gap-1 mb-5 flex-wrap">
        {pills.map(([k, label, n]) => (
          <button
            key={k}
            data-testid={`cohort-pill-${k}`}
            onClick={() => setCohort(k)}
            className={`px-3 py-1.5 text-xs rounded transition ${cohort === k ? "bg-white/10 text-neutral-100" : "text-neutral-500 hover:text-neutral-300"}`}
          >
            {label} <span className="text-neutral-600 ml-1">{n}</span>
          </button>
        ))}
      </div>
      <div className="grid gap-1" data-testid="cohort-rows">
        {loading && <p className="text-neutral-600 text-xs py-4">Loading…</p>}
        {!loading && data?.rows?.length === 0 && (
          <p className="text-neutral-600 text-xs py-4 italic">
            {cohort === "dormant" ? "No-one's gone quiet yet." : `No-one in ${cohort} yet.`}
          </p>
        )}
        {!loading && data?.rows?.map((r) => (
          <div
            key={r.user_id}
            data-testid={`cohort-row-${r.user_id}`}
            className="grid grid-cols-12 gap-3 items-center py-2 px-3 hover:bg-white/[0.03] text-xs border-b border-neutral-900/60"
          >
            <div className="col-span-4 text-neutral-200 truncate">{r.name || "—"}</div>
            <div className="col-span-3 text-neutral-500 truncate">{r.wa_number || r.email || "—"}</div>
            <div className="col-span-2 text-neutral-400 text-right">
              {r.total_reflections} ref · {r.total_messages} msg
            </div>
            <div className="col-span-2 text-neutral-500 text-right">
              {r.days_since_last === 0 ? "today" :
                r.days_since_last === 1 ? "1d" :
                r.days_since_last != null ? `${r.days_since_last}d` : "—"}
            </div>
            <div className="col-span-1 text-right">
              <span className="text-[10px] tracking-wider text-neutral-600 uppercase">{r.cohort}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}



function ConvRow({ c, onOpen }) {
  const intentColor = {
    match: "#A7F3D0",
    practice: "#A7F3D0",
    mindset: "#FCD34D",
    question: "#93C5FD",
    technical: "#FCA5A5",
    unknown: "#9CA3AF",
  }[c.intent] || "#9CA3AF";
  return (
    <div
      className="surface-1 p-4 cursor-pointer hover:bg-white/[0.02] transition-colors"
      data-testid={`conv-row-${c.reflection_id}`}
      onClick={() => onOpen(c.reflection_id)}
    >
      <div className="flex items-center justify-between text-xs text-neutral-500 mb-2">
        <span className="font-mono">{c.wa_number || "(web)"} {c.display_name ? `· ${c.display_name}` : ""}</span>
        <span>{fmtTime(c.last_at)}</span>
      </div>
      <div className="flex items-center gap-2 mb-2 text-xs">
        <span className="px-2 py-0.5 rounded" style={{ background: "rgba(255,255,255,0.06)", color: intentColor }}>{c.intent}</span>
        <span className="text-neutral-500">{c.status}</span>
        <span className="text-neutral-500">· {c.message_count} msgs</span>
        {c.has_report && <span className="px-2 py-0.5 rounded bg-emerald-900/40 text-emerald-300">report</span>}
        {c.feedback === "good" && <span className="px-2 py-0.5 rounded bg-emerald-900/50 text-emerald-300">👍</span>}
        {c.feedback === "bad" && <span className="px-2 py-0.5 rounded bg-red-900/50 text-red-300">👎</span>}
        {c.flagged && <span className="px-2 py-0.5 rounded bg-amber-900/50 text-amber-300">🚩 flagged</span>}
      </div>
      <p className="text-sm text-neutral-300 truncate"><span className="text-neutral-500">player:</span> {c.last_message || "—"}</p>
      <p className="text-sm text-neutral-300 truncate"><span className="text-neutral-500">gully :</span> {c.last_reply || "—"}</p>
      {c.admin_note && <p className="text-xs text-amber-300 mt-2 italic truncate">note: {c.admin_note}</p>}
    </div>
  );
}

function ConvDetail({ reflectionId, onClose }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/conversations/${reflectionId}`);
      setD(r.data);
      setNote(r.data?.feedback?.note || "");
    } catch (e) {
      toast.error("Could not load conversation");
    }
  }, [reflectionId]);

  useEffect(() => { load(); }, [load]);

  const saveFb = async (patch) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/conversations/${reflectionId}/feedback`, patch);
      setD((cur) => ({ ...cur, feedback: data.feedback }));
      toast.success("Saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setBusy(false);
    }
  };

  if (!d) {
    return (
      <div className="fixed inset-0 bg-black/85 backdrop-blur z-50 flex items-center justify-center p-4">
        <div className="text-neutral-400">Loading conversation…</div>
      </div>
    );
  }

  const fb = d.feedback || {};
  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur z-50 overflow-y-auto" data-testid="conv-detail-modal">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="label-eyebrow mb-1">conversation</p>
            <h2 className="font-display text-2xl">{d.wa_number || "(web)"} {d.display_name ? `· ${d.display_name}` : ""}</h2>
            <p className="text-xs text-neutral-500 mt-1 font-mono">{d.reflection_id} · {d.intent} · {d.status}</p>
          </div>
          <button onClick={onClose} className="btn-ghost text-sm" data-testid="conv-close-btn">Close</button>
        </div>

        {/* Errors */}
        {d.errors?.length > 0 && (
          <div className="bento-card mb-6" style={{ borderColor: "var(--warning)" }}>
            <p className="label-eyebrow mb-2" style={{ color: "var(--warning)" }}>errors ({d.errors.length})</p>
            {d.errors.map((e, i) => (
              <p key={i} className="text-xs text-red-300 font-mono">{(e.created_at || "").slice(11, 19)} · {e.kind} · {(e.summary || "").slice(0, 200)}</p>
            ))}
          </div>
        )}

        {/* Feedback controls */}
        <div className="bento-card mb-6">
          <p className="label-eyebrow mb-3">founder feedback</p>
          <div className="flex flex-wrap gap-2 mb-4" data-testid="feedback-controls">
            <button data-testid="fb-good-btn" disabled={busy} onClick={() => saveFb({ rating: fb.rating === "good" ? null : "good" })}
              className={`btn-ghost text-xs ${fb.rating === "good" ? "border-emerald-500 text-emerald-300" : ""}`}>
              👍 Good answer
            </button>
            <button data-testid="fb-bad-btn" disabled={busy} onClick={() => saveFb({ rating: fb.rating === "bad" ? null : "bad" })}
              className={`btn-ghost text-xs ${fb.rating === "bad" ? "border-red-500 text-red-300" : ""}`}>
              👎 Bad answer
            </button>
            <button data-testid="fb-flag-btn" disabled={busy} onClick={() => saveFb({ flagged: !fb.flagged })}
              className={`btn-ghost text-xs ${fb.flagged ? "border-amber-500 text-amber-300" : ""}`}>
              🚩 {fb.flagged ? "Flagged" : "Flag for review"}
            </button>
          </div>
          <textarea
            data-testid="fb-note-input"
            placeholder="Internal note…"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            className="input-dark w-full text-sm"
          />
          <div className="flex justify-end mt-2">
            <button data-testid="fb-save-note-btn" disabled={busy} onClick={() => saveFb({ note })} className="btn-primary text-xs">Save note</button>
          </div>
          {fb.by && <p className="text-xs text-neutral-600 mt-3">last edited {fmtTime(fb.updated_at)} by {fb.by}</p>}
        </div>

        {/* Report indicator */}
        {d.report && (
          <div className="bento-card mb-6" style={{ borderColor: "var(--highlight)" }}>
            <p className="label-eyebrow mb-1" style={{ color: "var(--highlight)" }}>report generated</p>
            <p className="text-sm text-neutral-300">{d.report.what_happened?.slice(0, 200) || "—"}</p>
            <p className="text-xs text-neutral-500 mt-2">{d.report.report_id}</p>
          </div>
        )}

        {/* Messages */}
        <p className="label-eyebrow mb-3">messages ({d.messages.length})</p>
        <div className="space-y-3" data-testid="conv-messages">
          {d.messages.map((m, i) => (
            <div key={i} className={`p-3 rounded ${m.role === "player" ? "bg-white/[0.03]" : "bg-emerald-900/10 border-l-2 border-emerald-700"}`}>
              <div className="flex items-center justify-between text-xs text-neutral-500 mb-1">
                <span>{m.role}</span>
                <span>{(m.timestamp || "").slice(11, 19) || ""}</span>
              </div>
              <p className="text-sm text-neutral-200 whitespace-pre-wrap">{m.content}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function LegacyAdmin() {
  const [data, setData] = useState(null);
  const [monitor, setMonitor] = useState(null);
  const [convs, setConvs] = useState([]);
  const [openRefl, setOpenRefl] = useState(null);
  const [phone, setPhone] = useState("");
  const [lookup, setLookup] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState("all"); // all | flagged | bad | good

  const loadAll = useCallback(async () => {
    try {
      const [a, b, c] = await Promise.all([
        api.get("/admin/dashboard"),
        api.get("/admin/monitor"),
        api.get("/admin/conversations?limit=50"),
      ]);
      setData(a.data); setMonitor(b.data); setConvs(c.data.conversations);
    } catch (e) {
      setErr(e.response?.status === 403 ? "Admin access required." : "Could not load.");
    }
  }, []);

  useEffect(() => {
    loadAll();
    const t = setInterval(loadAll, 30000); // auto-refresh every 30s
    return () => clearInterval(t);
  }, [loadAll]);

  const doLookup = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/lookup?phone=${encodeURIComponent(phone)}`);
      setLookup(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Not found");
      setLookup(null);
    } finally {
      setBusy(false);
    }
  };

  const togglePaid = async (newPaid) => {
    try {
      await api.post("/admin/set-paid", { phone, is_paid: newPaid });
      toast.success(`Marked ${newPaid ? "paid" : "free"}`);
      doLookup();
    } catch {
      toast.error("Failed to update");
    }
  };

  if (err) return <div className="grain min-h-screen"><Nav /><div className="max-w-3xl mx-auto px-6 py-16 text-neutral-500">{err}</div></div>;
  if (!data || !monitor) return <div className="grain min-h-screen"><Nav /><div className="max-w-3xl mx-auto px-6 py-16 text-neutral-500">Loading…</div></div>;

  const t = data.totals;
  const filteredConvs = convs.filter((c) => {
    if (filter === "flagged") return c.flagged;
    if (filter === "bad") return c.feedback === "bad";
    if (filter === "good") return c.feedback === "good";
    return true;
  });

  return (
    <div className="grain min-h-screen">
      <Nav />
      <div className="max-w-6xl mx-auto px-6 py-12">
        <p className="label-eyebrow mb-2">admin</p>
        <h1 className="font-display font-light text-3xl sm:text-4xl tracking-tight mb-6">WhatsApp Legacy Archive</h1>
        <div className="mb-10 p-4 rounded-lg border border-amber-900/40 bg-amber-950/20" data-testid="legacy-archive-banner">
          <p className="text-xs uppercase tracking-widest text-amber-400 mb-1">historical reference only</p>
          <p className="text-sm text-amber-100/80 leading-relaxed">
            Everything below reads from WhatsApp-era collections. WhatsApp inbound/outbound is dormant
            (Meta ban). Use these for historical reference — do <strong>not</strong> use them for current
            decision-making. The live web product lives at{" "}
            <Link to="/admin" className="underline hover:text-amber-50" data-testid="legacy-back-to-web">/admin</Link>.
          </p>
        </div>

        {/* Realtime monitor — LEGACY */}
        <p className="label-eyebrow mb-4">today &amp; last 24h <span className="text-amber-500/80 normal-case tracking-normal">(WhatsApp legacy)</span></p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px mb-10" style={{ background: "var(--border-default)" }} data-testid="monitor-stats">
          {[
            ["wa users", monitor.total_wa_users],
            ["active today", monitor.active_conversations_today],
            ["msgs today", monitor.messages_today],
            ["avg reply", monitor.avg_response_ms != null ? `${(monitor.avg_response_ms / 1000).toFixed(1)}s` : "—"],
          ].map(([k, v]) => (
            <div key={k} className="surface-1 p-5" data-testid={`mstat-${k.replace(' ', '-')}`}>
              <p className="label-eyebrow mb-1">{k}</p>
              <p className="font-display text-2xl font-light">{v}</p>
            </div>
          ))}
        </div>

        {/* Error counts */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-px mb-10" style={{ background: "var(--border-default)" }} data-testid="error-stats">
          {Object.entries(monitor.errors_24h).map(([k, v]) => (
            <div key={k} className="surface-1 p-4" data-testid={`err-${k}`}>
              <p className="label-eyebrow mb-1" style={{ color: v > 0 ? "var(--warning)" : undefined }}>{k.replace(/_/g, " ")}</p>
              <p className="font-display text-2xl font-light" style={{ color: v > 0 ? "var(--warning)" : undefined }}>{v}</p>
            </div>
          ))}
        </div>

        {/* User cohorts — observational, not vanity. Returning is the trust signal. */}
        <Cohorts />

        {/* Gully PM — internal founder intelligence (deterministic v1) */}
        <GullyPMTab />

        {/* Founder Intelligence — weekly auto-generated insights from real conversations */}
        <IntelTab />

        {/* Weekly Reviews · Phase 1 — generate-only preview before WhatsApp delivery */}
        <WeeklyReviewsTab />

        {/* Video Review Dashboard — usage, value signals, feedback, demand, insights, prospects */}
        <VideoDashboardTab />

        {/* Ambassador Pipeline — leading indicator + cohorts + top referrers */}
        <AmbassadorDashboardTab />

        {/* Manual WhatsApp check-in tool — founder-only, never automated. */}
        <CheckinTool />

        {/* Ambassador Spotlight — one-click IG story card for top ambassadors */}
        <SpotlightTab />

        {/* Conversation list */}
        <div className="flex items-center justify-between mb-4">
          <p className="label-eyebrow">latest conversations</p>
          <div className="flex gap-2 text-xs">
            {[["all", "All"], ["flagged", "🚩 Flagged"], ["bad", "👎 Bad"], ["good", "👍 Good"]].map(([k, l]) => (
              <button
                key={k}
                data-testid={`filter-${k}`}
                onClick={() => setFilter(k)}
                className={`px-3 py-1 rounded ${filter === k ? "bg-white/10 text-neutral-100" : "text-neutral-500 hover:text-neutral-300"}`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
        <div className="grid gap-2 mb-12" data-testid="conv-list">
          {filteredConvs.length === 0 && <p className="text-neutral-600 text-sm py-6">No conversations match.</p>}
          {filteredConvs.map((c) => (
            <ConvRow key={c.reflection_id} c={c} onOpen={(id) => setOpenRefl(id)} />
          ))}
        </div>

        {/* Legacy user lookup */}
        <div className="bento-card mb-12">
          <p className="label-eyebrow mb-4">user lookup</p>
          <div className="flex gap-3 mb-6">
            <input data-testid="admin-lookup-input" className="input-dark flex-1" placeholder="WhatsApp number (e.g. +91…)" value={phone} onChange={(e) => setPhone(e.target.value)} />
            <button data-testid="admin-lookup-btn" onClick={doLookup} disabled={busy || !phone} className="btn-primary text-sm">Lookup</button>
          </div>
          {lookup && (
            <div data-testid="admin-lookup-result">
              <p className="font-display text-lg mb-2">{lookup.whatsapp_user.display_name || "—"} · {lookup.whatsapp_user.wa_number}</p>
              <p className="text-sm text-neutral-400 mb-4">
                Reports: {lookup.whatsapp_user.reports_count} · Messages: {lookup.whatsapp_user.total_messages} · Paid: {String(lookup.whatsapp_user.is_paid)}
              </p>
              <div className="flex gap-2 mb-6">
                <button onClick={() => togglePaid(true)} className="btn-ghost text-xs">Mark paid</button>
                <button onClick={() => togglePaid(false)} className="btn-ghost text-xs">Mark free</button>
              </div>
              <details>
                <summary className="cursor-pointer text-xs text-neutral-500">Recent reports ({lookup.reports.length})</summary>
                <div className="mt-4 space-y-2">
                  {lookup.reports.map((r) => (
                    <p key={r.report_id} className="text-sm text-neutral-300">
                      {(r.created_at || "").slice(0, 10)} · {r.what_happened?.slice(0, 80)}…
                    </p>
                  ))}
                </div>
              </details>
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="bento-card">
            <p className="label-eyebrow mb-4">totals</p>
            <div className="space-y-1 text-sm text-neutral-300">
              <div>users: {t.users}</div>
              <div>whatsapp users: {t.whatsapp_users}</div>
              <div>reports: {t.reports}</div>
              <div>reflections: {t.reflections}</div>
              <div>wa messages: {t.whatsapp_messages}</div>
            </div>
          </div>

          <div className="bento-card">
            <p className="label-eyebrow mb-4">most common issues</p>
            <div className="space-y-2">
              {data.common_issues.map((i) => (
                <div key={i.issue} className="flex justify-between text-sm">
                  <span className="text-neutral-300">{i.issue}</span>
                  <span className="text-neutral-500">{i.count}</span>
                </div>
              ))}
              {data.common_issues.length === 0 && <p className="text-neutral-600 text-sm">No issues tagged yet.</p>}
            </div>
          </div>
        </div>
      </div>

      {openRefl && <ConvDetail reflectionId={openRefl} onClose={() => { setOpenRefl(null); loadAll(); }} />}
    </div>
  );
}
